import type { AgentEdge, AgentNode } from '@saaga/shared/interfaces/agency-node.interface'
import { noop } from '@saaga/shared/utils/noop.util'
import { useReactFlow } from '@xyflow/react'
import { useRef, useState } from 'react'

import { useAgencyFlow } from './agency-flow.hook'
import { useAgencyFlowCanvasDialogs } from './use-agency-flow-canvas-dialogs.hook'
import {
  useCanvasInitialFitView,
  useCanvasOnChange,
  useDeleteHandlerRef,
} from './use-agency-flow-canvas-effects.hook'
import { useAgencyFlowCanvasHandlers } from './use-agency-flow-canvas-handlers.hook'

type DeleteHandler = (nodeId: string) => void

export const useAgencyFlowCanvas = (
  initialNodeList?: AgentNode[],
  initialEdgeList?: AgentEdge[],
  onChange?: (nodeList: AgentNode[], edgeList: AgentEdge[]) => void
) => {
  const { fitView, screenToFlowPosition } = useReactFlow()
  const agencyFlow = useAgencyFlow(initialNodeList, initialEdgeList)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const deleteHandlerRef = useRef<DeleteHandler>(noop)
  const dialogs = useAgencyFlowCanvasDialogs(agencyFlow)
  const canvasHandlers = useAgencyFlowCanvasHandlers({
    agencyFlow,
    dialogs,
    fitView,
    screenToFlowPosition,
    setIsDrawerOpen,
    isDrawerOpen,
  })

  useCanvasOnChange({ nodeList: agencyFlow.nodeList, edgeList: agencyFlow.edgeList, onChange })
  useCanvasInitialFitView({ fitView })
  useDeleteHandlerRef({ deleteHandlerRef, handleDeleteRequest: dialogs.handleDeleteRequest })

  return {
    agencyFlow,
    isDrawerOpen,
    deleteDialogState: dialogs.deleteDialogState,
    edgePopoverState: dialogs.edgePopoverState,
    deleteHandlerRef,
    handlers: {
      ...canvasHandlers,
      handleDeleteConfirm: dialogs.handleDeleteConfirm,
      handleEdgeLabelSave: dialogs.handleEdgeLabelSave,
      handleEdgePopoverOpenChange: dialogs.handleEdgePopoverOpenChange,
      closeDeleteDialog: dialogs.closeDeleteDialog,
    },
  }
}
